"use client";

import {
  type OrbState,
  ThinkingOrb,
  type ThinkingOrbProps,
} from "thinking-orbs";
import { cn } from "@/lib/utils";

type LoadingIndicatorSize = "xs" | "sm" | "md" | "lg" | "xl" | number;

type LoadingIndicatorProps = Omit<
  ThinkingOrbProps,
  "aria-label" | "size" | "state"
> & {
  ariaLabel?: string;
  size?: LoadingIndicatorSize;
  state?: OrbState;
};

const SIZE_PRESETS = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 32,
  xl: 40,
} as const;

function resolveSize(size: LoadingIndicatorSize) {
  return typeof size === "number" ? size : SIZE_PRESETS[size];
}

export function LoadingIndicator({
  ariaLabel = "Loading",
  className,
  size = "sm",
  speed = 1,
  state = "working",
  style,
  ...props
}: LoadingIndicatorProps) {
  const resolvedSize = resolveSize(size);
  const presetSize = resolvedSize <= 24 ? 20 : 64;

  return (
    <ThinkingOrb
      aria-label={ariaLabel}
      className={cn("shrink-0 text-current", className)}
      size={presetSize}
      speed={speed}
      state={state}
      style={{ ...style, width: resolvedSize, height: resolvedSize }}
      {...props}
    />
  );
}
